import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { updateEpcr, useEpcr } from "../../reducers/epcrSlice";
import useNetwork from "../../common/hooks/networkHook";
import { addEpcrNote, strikeEpcrNote, useEpcrNotes } from "../../reducers/epcrNotesSlice";
import { addEpcrHistory, removeEpcrHistory, useEpcrHistory } from "../../reducers/epcrHistorySlice";
import { addEpcrMedication, removeEpcrMedication, useEpcrMedications } from "../../reducers/epcrMedicationSlice";
import { addEpcrAllergy, removeEpcrAllergy, useEpcrAllergies } from "../../reducers/epcrAllergySlice";
import { addEpcrVital, removeEpcrVital, updateEpcrVital, useEpcrVitals } from "../../reducers/epcrVitalSlice";
import { deepMerge, readField, writeField } from "../../common/utils/objectUtilities";
import { addEpcrProcedure, removeEpcrProcedure, useEpcrProdcedures } from "../../reducers/epcrProcedureSlice";
import { addEpcrTreatmentMedication, removeEpcrTreatmentMedicaiton, useEpcrTreatmentMedicationDefs, useEpcrTreatmentMedications } from "../../reducers/epcrTreatmentMedicationSlice";
import useClientConfig from "../../common/hooks/clientConfigHook";
import { nowISOString } from "../../common/utils/dateUtils";
import { deepClone } from "../../common/utils/objectUtilities";

const EpcrEditorContext = createContext({});

export const EpcrUpdateContext = ({ pcrid, site, level, type, children }) => {
    const network = useNetwork();
    const epcr = useEpcr(pcrid);
    const clientConfig = useClientConfig();

    const history = useEpcrHistory(pcrid);
    const meds = useEpcrMedications(pcrid);
    const allergies = useEpcrAllergies(pcrid);
    const vitals = useEpcrVitals(pcrid);
    const procedures = useEpcrProdcedures(pcrid);
    const rawTreatmentMeds = useEpcrTreatmentMedications(pcrid);
    const treatmentMedDefs = useEpcrTreatmentMedicationDefs();
    const treatmentMeds = useMemo(() => rawTreatmentMeds.map(med => ({ ...med, medstr: treatmentMedDefs.find(def => def.id === med.medicationid)?.name ?? '' })), [rawTreatmentMeds, treatmentMedDefs]);
    const notes = useEpcrNotes(pcrid);

    const update = useCallback((field, value) => updateEpcr(network, { id: pcrid, [field]: value }), [network, pcrid]);

    const addHistory = useCallback(history => addEpcrHistory(network, { id: crypto.randomUUID(), ...history, pcrid }), [network, pcrid]);
    const removeHistory = useCallback(history => removeEpcrHistory(network, history), [network]);

    const addMedication = useCallback(medication => addEpcrMedication(network, { id: crypto.randomUUID(), ...medication, pcrid }), [network, pcrid]);
    const removeMedication = useCallback(medication => removeEpcrMedication(network, medication), [network]);

    const addAllergy = useCallback(allergy => addEpcrAllergy(network, { id: crypto.randomUUID(), ...allergy, pcrid }), [network, pcrid]);
    const removeAllergy = useCallback(allergy => removeEpcrAllergy(network, allergy), [network]);

    const addVital = useCallback(newVital => addEpcrVital(network, { ...newVital, pcrid }), [network, pcrid]);
    const updateVital = useCallback((vital, field, value) => {
        if (field === 'values') {
            return updateEpcrVital(network, { ...vital, values: deepMerge({}, vital.values, value) })
        } else {
            return updateEpcrVital(network, { ...vital, [field]: value });
        }
    }, [network]);
    const removeVital = useCallback(vitalID => removeEpcrVital(network, vitalID, pcrid), [network, pcrid]);

    const addTreatmentMed = useCallback(newMed => addEpcrTreatmentMedication(network, { id: crypto.randomUUID(), ...newMed, pcrid }), [network, pcrid]);
    const removeTreatmentMed = useCallback(med => removeEpcrTreatmentMedicaiton(network, med), [network]);

    const addProcedure = useCallback(procedure => addEpcrProcedure(network, { id: crypto.randomUUID(), ...procedure, pcrid }), [network, pcrid]);
    const removeProcedure = useCallback(procedure => removeEpcrProcedure(network, procedure), [network]);

    const addNote = useCallback((text, author) => addEpcrNote(network, { pcrid, note: text, username: author, autogenerated: 0, id: crypto.randomUUID(), timestamp: nowISOString(), tz: clientConfig.tz }), [network, pcrid, clientConfig.tz]);
    const strikeNote = useCallback((note, strike_reason, strike_username, strike_timestamp) => strikeEpcrNote(network, { id: note.id, pcrid, strike_reason, strike_username, strike_timestamp }), [network, pcrid]);

    const value = useMemo(() => ({ intent: 'edit', epcr, site, level, type, history, meds, allergies, vitals, procedures, treatmentMeds, notes,
        update, addNote, strikeNote, addHistory, removeHistory, addMedication, removeMedication, addAllergy, removeAllergy,
        addVital, updateVital, removeVital, addTreatmentMed: addTreatmentMed, removeTreatmentMed, addProcedure, removeProcedure }),
        [ epcr, site, level, type, history, meds, allergies, vitals, procedures, treatmentMeds, notes, update, addNote, strikeNote,
          addHistory, removeHistory, addMedication, removeMedication, addAllergy, removeAllergy,
          addVital, updateVital, removeVital, addTreatmentMed, removeTreatmentMed, addProcedure, removeProcedure ]);

    return <EpcrEditorContext.Provider value={value}>
        {children}
    </EpcrEditorContext.Provider>
}

export const EpcrSearchContext = ({ children }) => {
    const [epcr, setEpcr] = useState({});
    const update = useCallback((field, value) => setEpcr(epcr => {
        const newValue = deepClone(epcr);
        const origValue = readField(newValue, field);
        if (origValue && typeof origValue === 'object') {
            writeField(newValue, field, deepMerge({}, origValue, value));
        } else {
            writeField(newValue, field, value);
        }
        Object.keys(newValue).forEach(f => {
            if (newValue[f] === null || newValue[f] === undefined || newValue[f] === '') {
                delete newValue[f];
            } 
        })
        return newValue;
    }), []);
    const clear = useCallback(() => setEpcr({}), []);

    const value = useMemo(() => ({ intent: 'search', epcr, site: epcr.site, level: epcr.level, type: epcr.type, update, clear }), [epcr, update, clear ]);

    return <EpcrEditorContext.Provider value={value}>
        {children}
    </EpcrEditorContext.Provider>
}

export const useEpcrEditorIntent = () => {
    return useContext(EpcrEditorContext).intent;
}

export const useEpcrEditorEpcr = () => {
    const context = useContext(EpcrEditorContext);

    return useMemo(() => ({ epcr: context.epcr, site: context.site, level: context.level, type: context.type, update: context.update, clear: context.clear }), [context.epcr, context.site, context.level, context.type, context.update, context.clear]);
}

export const useEpcrEditorNotes = () => {
    const context = useContext(EpcrEditorContext);

    if (context.intent !== 'edit') {
        throw new Error(`useEpcrEditorNotes is only available under the edit provider - got ${context.intent}`);
    }

    return useMemo(() => ({ notes: context.notes, addNote: context.addNote, strikeNote: context.strikeNote }), [context.notes, context.addNote, context.strikeNote]);
}

export const useEpcrEditorHistory = () => {
    const context = useContext(EpcrEditorContext);

    if (context.intent !== 'edit') {
        throw new Error(`useEpcrEditorHistory is only available under the edit provider - got ${context.intent}`);
    }

    return useMemo(() => ({ history: context.history,addHistory: context.addHistory, removeHistory: context.removeHistory, 
        meds: context.meds, addMedication: context.addMedication, removeMedication: context.removeMedication,
        allergies: context.allergies, addAllergy: context.addAllergy, removeAllergy: context.removeAllergy
    }), [context.history, context.addHistory, context.removeHistory, 
        context.meds, context.addMedication, context.removeMedication,
        context.allergies, context.addAllergy, context.removeAllergy]);
}

export const useEpcrEditorVitals = () => {
    const context = useContext(EpcrEditorContext);

    if (context.intent !== 'edit') {
        throw new Error(`useEpcrEditorVitals is only available under the edit provider - got ${context.intent}`);
    }

    return useMemo(() => ({ vitals: context.vitals, addVital: context.addVital, updateVital: context.updateVital, removeVital: context.removeVital }),
                [context.vitals, context.addVital, context.updateVital, context.removeVital]);
}

export const useEpcrEditorTreatmentMeds = () => {
    const context = useContext(EpcrEditorContext);

    if (context.intent !== 'edit') {
        throw new Error(`useEpcrEditorTreatmentMeds is only available under the edit provider - got ${context.intent}`);
    }

    return useMemo(() => ({ treatmentMeds: context.treatmentMeds, addTreatmentMed: context.addTreatmentMed, removeTreatmentMed: context.removeTreatmentMed }), [context.treatmentMeds, context.addTreatmentMed, context.removeTreatmentMed]);
}

export const useEpcrEditorProcedures = () => {
    const context = useContext(EpcrEditorContext);

    if (context.intent !== 'edit') {
        throw new Error(`useEpcrEditorProcedures is only available under the edit provider - got ${context.intent}`);
    }

    return useMemo(() => ({ procedures: context.procedures, addProcedure: context.addProcedure, removeProcedure: context.removeProcedure }), [context.procedures, context.addProcedure, context.removeProcedure]);
}